import { useCallback } from 'react';
import { camelize } from 'src/utils/camelize';

import { usePermissionCheck } from '../shared/usePermissionCheck';

export type Permission = {
  editLogEntries: string;
  viewTasks: string;
  viewOtherTimeClock: string;
  clockIn: string;
  viewStaffUserMenu: string;
  viewStaffTaskManagement: string;
  viewTimeTracking: string;
  viewApiKeys: string;
  viewWebhooks: string;
  exportTimeClockReport: string;
  viewAppointmentMembers: string;
  editAppointmentMembers: string;
  checkInAppointment: string;
  globalCheckIn: string;
  viewOthersScheduling: string;
  editNoteFields: string;
  viewBillingInfo: string;
  viewMessages: string;
};

interface PermissionCheckArgs {
  can?: keyof Permission;
  cannot?: keyof Permission;
  object?: object;
}

export const usePermissions = () => {
  const permission = usePermissionCheck();

  const permissionCheck = useCallback(
    ({ can, cannot, object }: PermissionCheckArgs): boolean => {
      const check = can || cannot;
      let hasPermission =
        check &&
        (object
          ? permission[`can${camelize(check, { pascalCase: true })}`](object)
          : permission[`can${camelize(check, { pascalCase: true })}`]);

      if (cannot) {
        hasPermission = !hasPermission;
      }

      return hasPermission;
    },
    [permission]
  );

  return { permissionCheck };
};

interface PermissionCheckProps {
  children: React.ReactNode;
  noPermission?: React.ReactNode;
  can?: keyof Permission;
  cannot?: keyof Permission;
  object?: object;
}

export const PermissionCheck: React.FC<PermissionCheckProps> = ({
  can,
  cannot,
  object,
  children,
  noPermission,
}) => {
  const { permissionCheck } = usePermissions();
  const hasPermission = permissionCheck({ can, cannot, object });

  if (!hasPermission) {
    return <>{noPermission}</> || null;
  }

  return <>{children}</>;
};

